import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Magnetic } from "./effects";

const links = [
  { label: "Work", href: "#showreel" },
  { label: "Portfolio", href: "#short-form" },
  { label: "Services", href: "#services" },
  { label: "FAQ", href: "#faq" },
];

export const scrollTo = (target) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(target, { offset: -70, duration: 1.4 });
  } else {
    document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
  }
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (window.location.pathname !== "/") {
      window.location.href = "/" + href;
      return;
    }
    scrollTo(href);
  };

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-[80] transition-all duration-500 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <a
          href="/"
          data-testid="nav-brand-link"
          onClick={(e) => {
            if (window.location.pathname === "/") {
              e.preventDefault();
              scrollTo("body");
            }
          }}
          className="flex items-center gap-3 group"
        >
          <img src="/logo.png" alt="Sheen Creates logo" data-testid="nav-logo-img" className="h-9 w-9 rounded-full border border-[#D4AF37]/40 object-cover" />
          <span className="font-display text-lg font-semibold tracking-[0.18em] group-hover:text-[#F5DE81] transition-colors duration-300">
            SHEEN
          </span>
          <span className="hidden sm:block text-[10px] uppercase tracking-[0.25em] text-slate-500 border-l border-white/10 pl-3">
            Nausheen Fatima
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop-links">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              onClick={(e) => go(e, l.href)}
              className="text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-white transition-colors duration-300 relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#D4AF37] group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Magnetic strength={0.25}>
            <button
              data-testid="nav-book-call-btn"
              onClick={(e) => go(e, "#contact")}
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#D4AF37] hover:bg-[#C9A227] text-white text-xs font-semibold tracking-wide px-5 py-2.5 transition-colors duration-300 shadow-[0_0_24px_rgba(212,175,55,0.35)]"
            >
              Book a Call
            </button>
          </Magnetic>
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden glass border-t border-white/5 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  className="text-sm uppercase tracking-[0.2em] text-slate-300"
                >
                  {l.label}
                </a>
              ))}
              <button
                data-testid="nav-mobile-book-call-btn"
                onClick={(e) => go(e, "#contact")}
                className="mt-2 rounded-full bg-[#D4AF37] text-white text-sm font-semibold px-5 py-3"
              >
                Book a Call
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
